import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IconColourEnum } from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import { IconsEnum } from '../../../../shared-modules/shared-icons/models/icons.enum';
import { ISelectOption } from '../../../../shared/shared-models/custom-select/custom-select';
import { ISharedModalBasic } from '../../../../shared/shared-models/modals/shared-modal-basic';
import { IStore } from '../../../../shared/shared-models/store/store';
import { IModalDismissResult } from '../../../../shared/shared-services/overlay/modal-controller.service';
import { SharedModalUtils } from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import { getFieldErrorString } from '../../../../shared/utils/form/form.utils';
import { AutoOrderDefaultVisibleFields } from '../../../auto-ordering/models/auto-order-visible-fields';
import { AO_KEY_2_TITLE } from '../../../auto-ordering/models/old-models/auto-order-types';
import { _DEFAULT_APPLICATION_TOOLTIPS } from '../../../core/models';
import {
  ICrudOptions,
  IUserAccessFeatureAutoOrderingSettings,
} from '../../../user-access/models/user-access-features/access';
import { selectUserAccessFeatureAutoOrderingSettingsForSelectedStore } from '../../../user-access/user-access-store/user-access.selectors';
import { selectSelectedStore } from '../../../user-access/user-stores-store/user-store.selectors';
import {
  _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS,
  _DEFAULT_AUTO_ORDER_OPEN_ORDER_FILTER_SETTINGS,
  _DEFAULT_OPEN_ORDER_VIEW_SETTINGS,
  IAutoOrderAccountSettings,
  IOpenOrderFilterSettings,
  IOpenOrderSettings,
  IOpenOrderViewSettings,
  SettingsReferenceEnum,
} from '../../models';
import { TLineColour } from '../../models/line-colour/line-colour.interface';
import { IHasUnsavedChanges, UnsavedChangesService } from '../../services';
import {
  settingsSetSelected,
  settingStoreAOStoreOpenOrderSettings,
} from '../../store/setting.actions';
import {
  selectSelectedSetting,
  selectSelectedStoreLineColours,
  selectSelectedStoreOpenOrderSettings,
} from '../../store/setting.selectors';

@Component({
  selector: 'app-auto-order-ordering-settings',
  standalone: false,
  templateUrl: './feature-auto-order-open-order-settings.component.html',
  styleUrl: './feature-auto-order-open-order-settings.component.scss',
})
export class FeatureAutoOrderOpenOrderSettingsComponent
  implements OnInit, OnDestroy, IHasUnsavedChanges
{
  //#region INJECTORS
  private formBuilder = inject(FormBuilder);
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private unsavedChangesService = inject(UnsavedChangesService);

  //#endregion

  //#region PROPERTIES
  formOpenOrderSettings: FormGroup;
  fieldErrors: { [key: string]: string } = {};
  protected aoColumns: Record<string, ISelectOption> = {};
  protected selectedLineColors = signal<string[]>([]);

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _settingChanged: Signal<SettingsReferenceEnum> =
    this._store.selectSignal(selectSelectedSetting);
  private readonly _openOrderSettings: Signal<IOpenOrderSettings> = this._store.selectSignal(
    selectSelectedStoreOpenOrderSettings,
  );
  private readonly _uaOrderingSettings: Signal<IUserAccessFeatureAutoOrderingSettings> =
    this._store.selectSignal(selectUserAccessFeatureAutoOrderingSettingsForSelectedStore);
  private readonly _lineColours: Signal<TLineColour> = this._store.selectSignal(
    selectSelectedStoreLineColours,
  );

  protected readonly lineColourFilterOptions = computed<Record<string, ISelectOption>>(() => {
    const colours = this._lineColours() ?? {};
    const selected = this.selectedLineColors();
    const allLineColours = [
      { code: '0', description: 'No Line Colour', value: 'transparent' },
      ...Object.entries(colours).map(([key, colour]) => ({
        code: key,
        description: colour.description,
        value: colour.value,
      })),
    ];
    const sorted = allLineColours.sort((a, b) => a.description.localeCompare(b.description));
    const options: Record<string, ISelectOption> = {};
    sorted.forEach((lineColour) => {
      options[`lc-${lineColour.code}`] = {
        name: `${lineColour.description} (${lineColour.code})`,
        selected: selected.includes(lineColour.code),
        disabled: false,
        colour: lineColour.value,
      };
    });
    return options;
  });
  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const userAccess = this._uaOrderingSettings();
      const settings = this._openOrderSettings();
      this.initializeForms(userAccess, settings);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ ref }));
    });

    // Register with unsaved changes service
    this.unsavedChangesService.registerComponent(this);
  }

  //#endregion

  //#region ACCESSORS - OPEN ORDER SETTINGS

  get hasOpenOrderSettingsBeenEdited(): boolean {
    return this.formOpenOrderSettings?.dirty ?? false;
  }

  get hasOpenOrderSettingsErrors(): {
    errors: ValidationErrors;
    error: string;
    field: string;
  }[] {
    const errors: { errors: ValidationErrors; error: string; field: string }[] = [];

    // Validate account settings fields
    const stockDaysErrors = this.formOpenOrderSettings.controls['stockDays']?.errors;
    const growthErrors = this.formOpenOrderSettings.controls['growth']?.errors;
    const monthsHistoryErrors = this.formOpenOrderSettings.controls['monthsHistory']?.errors;
    const periodErrors = this.formOpenOrderSettings.controls['period']?.errors;

    if (stockDaysErrors) {
      errors.push({
        errors: stockDaysErrors,
        error: getFieldErrorString(stockDaysErrors, 'stockDays'),
        field: 'stockDays',
      });
    }
    if (growthErrors) {
      errors.push({
        errors: growthErrors,
        error: getFieldErrorString(growthErrors, 'growth'),
        field: 'growth',
      });
    }
    if (monthsHistoryErrors) {
      errors.push({
        errors: monthsHistoryErrors,
        error: getFieldErrorString(monthsHistoryErrors, 'monthsHistory'),
        field: 'monthsHistory',
      });
    }
    if (periodErrors) {
      errors.push({
        errors: periodErrors,
        error: getFieldErrorString(periodErrors, 'period'),
        field: 'period',
      });
    }

    // Add any custom validation errors
    Object.entries(this.fieldErrors).forEach(([field, error]) => {
      if (!errors.find((e) => e.field === field)) {
        errors.push({
          errors: { custom: error },
          error,
          field,
        });
      }
    });

    return errors;
  }

  get isOpenOrderSettingsSubmitDisabled(): boolean {
    return (
      this.canOpenOrderSettingsBeEdited === false ||
      (this.canOpenOrderSettingsBeEdited === true &&
        this.hasOpenOrderSettingsBeenEdited === false) ||
      (this.formOpenOrderSettings?.invalid ?? true) ||
      Object.keys(this.fieldErrors).length > 0
    );
  }

  get canOpenOrderSettingsBeEdited(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canEdit ?? false;
  }

  get canOpenOrderSettingsBeSubmitted(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canSubmit ?? false;
  }

  get canOpenOrderSettingsBeViewed(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSORS - USER ACCESS + STORE + SETTINGS

  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get orderingSettingsUserAccess(): IUserAccessFeatureAutoOrderingSettings {
    return this._uaOrderingSettings();
  }

  get openOrderSettings(): IOpenOrderSettings {
    return this._openOrderSettings();
  }

  //#endregion

  //#region ACCESSORS - FORM VALUES

  get filters(): IOpenOrderFilterSettings {
    return {
      suggestionGreaterThanZero:
        this.formOpenOrderSettings?.get('suggestionGreaterThanZero')?.value ?? false,
      orderQuantityGreaterThanZero:
        this.formOpenOrderSettings?.get('orderQuantityGreaterThanZero')?.value ?? false,
      purchaseOrders: this.formOpenOrderSettings?.get('purchaseOrders')?.value ?? 'unset',
      selectedLineColors: this.selectedLineColors(),
    };
  }

  get orderViewSettings(): IOpenOrderViewSettings {
    return {
      inclDaysNegative: this.formOpenOrderSettings?.get('inclDaysNegative')?.value ?? false,
      inclDaysZero: this.formOpenOrderSettings?.get('inclDaysZero')?.value ?? false,
      mainSupplierItems: this.formOpenOrderSettings?.get('mainSupplierItems')?.value ?? true,
      meetOrderLevel: this.formOpenOrderSettings?.get('meetOrderLevel')?.value ?? false,
      otherSupplierItems: this.formOpenOrderSettings?.get('otherSupplierItems')?.value ?? false,
      packSize: this.formOpenOrderSettings?.get('packSize')?.value ?? false,
      showLineColours: this.formOpenOrderSettings?.get('showLineColours')?.value ?? true,
      subtractPurchaseOrders:
        this.formOpenOrderSettings?.get('subtractPurchaseOrders')?.value ?? true,
    };
  }

  get accountSettings(): IAutoOrderAccountSettings {
    return {
      stockDays: this.formOpenOrderSettings?.get('stockDays')?.value ?? 30,
      growth: this.formOpenOrderSettings?.get('growth')?.value ?? 10,
      monthsHistory: this.formOpenOrderSettings?.get('monthsHistory')?.value ?? 3,
      period: this.formOpenOrderSettings?.get('period')?.value ?? 2,
    };
  }

  get exportQtyGreaterThanZero(): boolean {
    return this.formOpenOrderSettings?.get('exportQtyGreaterThanZero')?.value ?? false;
  }

  //#endregion

  //#region ACCESSORS - OPTIONS
  protected get aoExportOptions(): Record<string, ISelectOption> {
    if (!this.aoColumns) return {};

    return Object.fromEntries(
      Object.entries(this.aoColumns)
        .sort(([, a], [, b]) => {
          if (a.selected !== b.selected) {
            return a.selected ? -1 : 1;
          }
          if (a.disabled !== b.disabled) {
            return a.disabled ? 1 : -1;
          }
          return a.name.localeCompare(b.name);
        })
        .map(([key, value]) => [
          key,
          {
            name: value.name,
            selected: value.selected,
            disabled: value.disabled,
          },
        ]),
    );
  }

  protected get aoExportSelectedKeys(): string[] {
    return Object.keys(this.aoColumns ?? {}).filter((key) => this.aoColumns[key]?.selected);
  }

  //#endregion

  //#region ACCESSORS - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return this.hasOpenOrderSettingsBeenEdited;
  }

  //#endregion

  //#region LIFECYCLE HOOKS

  ngOnInit(): void {
    this.formOpenOrderSettings = this.formBuilder.group({});
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  //#endregion

  //#region FORM - CLICK HANDLERS

  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
      case 'openOrderSettings':
        this.revertOpenOrderSettings();
        break;
    }
  }

  onSubmitForm(form: string): void {
    const settings = this.openOrderSettings;
    let data: IOpenOrderSettings = { ...settings };

    switch (form) {
      case 'all':
      case 'openOrderSettings':
        if (!this.canOpenOrderSettingsBeSubmitted) break;
        data = {
          filters: this.filters,
          orderSettings: this.orderViewSettings,
          orderAccountSettings: this.accountSettings,
          exportQtyGreaterThanZero: this.exportQtyGreaterThanZero,
          selectedColumns: Object.entries(this.aoColumns)
            .filter(([, col]) => col.selected)
            .map(([key]) => key),
        };
        this.formOpenOrderSettings.markAsPristine();
        break;
      default:
        return;
    }

    this._store.dispatch(
      settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrder({ settings: data }),
    );
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(
    access: IUserAccessFeatureAutoOrderingSettings,
    settings: IOpenOrderSettings,
  ): void {
    this.buildOpenOrderSettingsForm(access?.openOrderSettings, settings);
  }

  private buildOpenOrderSettingsForm(access: ICrudOptions, settings: IOpenOrderSettings): void {
    if (access?.canView && this.formOpenOrderSettings.pristine) {
      const filters = settings?.filters ?? _DEFAULT_AUTO_ORDER_OPEN_ORDER_FILTER_SETTINGS;
      const orderSettings = settings?.orderSettings ?? _DEFAULT_OPEN_ORDER_VIEW_SETTINGS;
      const accountSettings =
        settings?.orderAccountSettings ?? _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS;
      const exportQty = settings?.exportQtyGreaterThanZero ?? false;

      const tempForm = this.formBuilder.group({});
      const disabled = !access?.canEdit;

      // Filters
      this.addFormControl(
        tempForm,
        'suggestionGreaterThanZero',
        filters.suggestionGreaterThanZero,
        disabled,
        [],
      );
      this.addFormControl(
        tempForm,
        'orderQuantityGreaterThanZero',
        filters.orderQuantityGreaterThanZero,
        disabled,
        [],
      );
      this.addFormControl(tempForm, 'purchaseOrders', filters.purchaseOrders, disabled, []);

      // Order View Settings
      this.addFormControl(
        tempForm,
        'inclDaysNegative',
        orderSettings.inclDaysNegative,
        disabled,
        [],
      );
      this.addFormControl(tempForm, 'inclDaysZero', orderSettings.inclDaysZero, disabled, []);
      this.addFormControl(
        tempForm,
        'mainSupplierItems',
        orderSettings.mainSupplierItems,
        disabled,
        [],
      );
      this.addFormControl(tempForm, 'meetOrderLevel', orderSettings.meetOrderLevel, disabled, []);
      this.addFormControl(
        tempForm,
        'otherSupplierItems',
        orderSettings.otherSupplierItems,
        disabled,
        [],
      );
      this.addFormControl(tempForm, 'packSize', orderSettings.packSize, disabled, []);
      this.addFormControl(tempForm, 'showLineColours', orderSettings.showLineColours, disabled, []);
      this.addFormControl(
        tempForm,
        'subtractPurchaseOrders',
        orderSettings.subtractPurchaseOrders,
        disabled,
        [],
      );

      // Account Settings with validation
      this.addFormControl(tempForm, 'stockDays', accountSettings.stockDays, disabled, [
        Validators.required,
        Validators.min(0),
        Validators.max(255),
      ]);
      this.addFormControl(tempForm, 'growth', accountSettings.growth, disabled, [
        Validators.required,
        Validators.min(-255),
        Validators.max(255),
      ]);
      this.addFormControl(tempForm, 'monthsHistory', accountSettings.monthsHistory, disabled, [
        Validators.required,
        Validators.min(1),
      ]);
      this.addFormControl(tempForm, 'period', accountSettings.period, disabled, [
        Validators.required,
        Validators.min(1),
        Validators.max(3),
      ]);

      // Export settings
      this.addFormControl(tempForm, 'exportQtyGreaterThanZero', exportQty, disabled, []);

      this.aoColumns = this.buildAoColumns(settings?.selectedColumns);
      this.selectedLineColors.set(filters.selectedLineColors ?? []);

      tempForm.markAsPristine();
      this.formOpenOrderSettings = tempForm;
    }
  }

  private addFormControl<T = unknown>(
    form: FormGroup,
    controlName: string,
    value: T,
    disabled: boolean,
    validators: ValidatorFn[] = [],
  ): void {
    form.addControl(controlName, this.formBuilder.control({ value, disabled }, validators));
  }

  private buildAoColumns(selectedColumns?: string[]): Record<string, ISelectOption> {
    const selected = selectedColumns ?? AutoOrderDefaultVisibleFields;
    const result: Record<string, ISelectOption> = {};
    const lockedColumns = new Set(['orderQty']);
    Object.entries(AO_KEY_2_TITLE).forEach(([key, name]) => {
      if (!lockedColumns.has(key)) {
        result[key] = {name, selected: selected.includes(key), disabled: false};
      }
    });
    return result;
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS

  private revertOpenOrderSettings(): void {
    const settings = this.openOrderSettings;
    const filters = settings?.filters ?? _DEFAULT_AUTO_ORDER_OPEN_ORDER_FILTER_SETTINGS;
    const orderSettings = settings?.orderSettings ?? _DEFAULT_OPEN_ORDER_VIEW_SETTINGS;
    const accountSettings = settings?.orderAccountSettings ?? _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS;

    this.formOpenOrderSettings.reset({
      // Filters
      suggestionGreaterThanZero: filters.suggestionGreaterThanZero,
      orderQuantityGreaterThanZero: filters.orderQuantityGreaterThanZero,
      purchaseOrders: filters.purchaseOrders,
      // Order View Settings
      inclDaysNegative: orderSettings.inclDaysNegative,
      inclDaysZero: orderSettings.inclDaysZero,
      mainSupplierItems: orderSettings.mainSupplierItems,
      meetOrderLevel: orderSettings.meetOrderLevel,
      otherSupplierItems: orderSettings.otherSupplierItems,
      packSize: orderSettings.packSize,
      showLineColours: orderSettings.showLineColours,
      subtractPurchaseOrders: orderSettings.subtractPurchaseOrders,
      // Account Settings
      stockDays: accountSettings.stockDays,
      growth: accountSettings.growth,
      monthsHistory: accountSettings.monthsHistory,
      period: accountSettings.period,
      // Export
      exportQtyGreaterThanZero: settings?.exportQtyGreaterThanZero ?? false,
    });

    this.aoColumns = this.buildAoColumns(this.openOrderSettings?.selectedColumns);
    this.selectedLineColors.set(this.openOrderSettings?.filters?.selectedLineColors ?? []);
    this.fieldErrors = {};
  }

  //#endregion

  //#region FORM - EVENT HANDLERS

  onFilterToggleChange(field: keyof IOpenOrderFilterSettings, value: boolean): void {
    this.formOpenOrderSettings.get(field)?.setValue(value);
    this.formOpenOrderSettings.markAsDirty();
  }

  onOrderSettingToggleChange(field: keyof IOpenOrderViewSettings, value: boolean): void {
    this.formOpenOrderSettings.get(field)?.setValue(value);
    this.formOpenOrderSettings.markAsDirty();
  }

  onExportQtyToggleChange(value: boolean): void {
    this.formOpenOrderSettings.get('exportQtyGreaterThanZero')?.setValue(value);
    this.formOpenOrderSettings.markAsDirty();
  }

  onLineColourFilterChange(selected: Record<string, ISelectOption>): void {
    this.selectedLineColors.set(
      Object.keys(selected)
        .filter((key: string) => selected[key].selected)
        .map((key: string) => key.replace('lc-', '')),
    );
    this.formOpenOrderSettings.markAsDirty();
  }

  onAoExportChange(selected: Record<string, ISelectOption>): void {
    Object.entries(this.aoColumns).forEach(([key, column]) => {
      this.aoColumns[key] = {
        ...column,
        selected: !!selected[key]?.selected,
      };
    });
    this.formOpenOrderSettings.markAsDirty();
  }

  onResetToDefaultClick(): void {
    const tempForm = this.formBuilder.group({});
    const disabled = !this.canOpenOrderSettingsBeEdited;

    // Filters
    this.addFormControl(
      tempForm,
      'suggestionGreaterThanZero',
      _DEFAULT_AUTO_ORDER_OPEN_ORDER_FILTER_SETTINGS.suggestionGreaterThanZero,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'orderQuantityGreaterThanZero',
      _DEFAULT_AUTO_ORDER_OPEN_ORDER_FILTER_SETTINGS.orderQuantityGreaterThanZero,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'purchaseOrders',
      _DEFAULT_AUTO_ORDER_OPEN_ORDER_FILTER_SETTINGS.purchaseOrders,
      disabled,
      [],
    );

    // Order View Settings
    this.addFormControl(
      tempForm,
      'inclDaysNegative',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.inclDaysNegative,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'inclDaysZero',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.inclDaysZero,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'mainSupplierItems',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.mainSupplierItems,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'meetOrderLevel',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.meetOrderLevel,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'otherSupplierItems',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.otherSupplierItems,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'packSize',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.packSize,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'showLineColours',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.showLineColours,
      disabled,
      [],
    );
    this.addFormControl(
      tempForm,
      'subtractPurchaseOrders',
      _DEFAULT_OPEN_ORDER_VIEW_SETTINGS.subtractPurchaseOrders,
      disabled,
      [],
    );

    // Account Settings
    this.addFormControl(
      tempForm,
      'stockDays',
      _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS.stockDays,
      disabled,
      [Validators.required, Validators.min(0), Validators.max(255)],
    );
    this.addFormControl(tempForm, 'growth', _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS.growth, disabled, [
      Validators.required,
      Validators.min(-255),
      Validators.max(255),
    ]);
    this.addFormControl(
      tempForm,
      'monthsHistory',
      _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS.monthsHistory,
      disabled,
      [Validators.required, Validators.min(1)],
    );
    this.addFormControl(tempForm, 'period', _DEFAULT_AUTO_ORDER_ACCOUNT_SETTINGS.period, disabled, [
      Validators.required,
      Validators.min(1),
      Validators.max(3),
    ]);

    // Export
    this.addFormControl(tempForm, 'exportQtyGreaterThanZero', false, disabled, []);

    this.selectedLineColors.set([]);
    tempForm.markAsDirty();
    this.formOpenOrderSettings = tempForm;
    this.fieldErrors = {};
  }

  //#endregion

  //#region VALIDATION

  validateField(field: keyof IAutoOrderAccountSettings, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const value = Number(target.value);
    let error: string | null = null;

    switch (field) {
      case 'stockDays':
        if (!target.value || value < 0 || value > 255) {
          error = 'Stock Days must be between 0 and 255';
        }
        break;

      case 'growth':
        if (!target.value || value < -255 || value > 255) {
          error = 'Growth Estimation must be between -255 and 255';
        }
        break;

      case 'monthsHistory':
        if (!target.value || value < 1) {
          error = 'Months of History must be at least 1';
        } else if (value >= 12 && this.accountSettings?.period !== 3) {
          error = 'Order History ≥ 12 requires "Last X Months"';
        }
        break;

      case 'period':
        if (!target.value || value < 1 || value > 3) {
          error = 'Please select a valid ordering method';
        }
        // Re-validate monthsHistory when period changes
        if (this.accountSettings?.monthsHistory >= 12 && value !== 3) {
          this.fieldErrors['monthsHistory'] = 'Order History ≥ 12 requires "Last X Months"';
        } else if (this.fieldErrors['monthsHistory'] && value === 3) {
          delete this.fieldErrors['monthsHistory'];
        }
        break;
    }

    if (error) {
      this.fieldErrors[field] = error;
    } else if (this.fieldErrors[field]) {
      delete this.fieldErrors[field];
    }
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  /**
   * Shows a modal asking the user if they want to discard unsaved changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion
}
